/**
 * Helper function to calculate distance between two points
 */
function getDistance(point1, point2) {
	return Math.hypot(point2.x - point1.x, point2.y - point1.y);
}

/**
 * Helper function to get center point of an item
 */
function getCenterPoint(item) {
	const [x, y, w, h] = item;
	return {
		x: x + w / 2,
		y: y + h / 2,
	};
}

/**
 * Helper function to check overlap between rectangles
 */
function checkOverlap(rect1, rect2, spacing) {
	const [x1, y1, w1, h1] = rect1;
	const [x2, y2, w2, h2] = rect2;

	return !(
		x1 + w1 + spacing - 1 < x2 ||
		x2 + w2 + spacing - 1 < x1 ||
		y1 + h1 + spacing - 1 < y2 ||
		y2 + h2 + spacing - 1 < y1
	);
}

/**
 * Helper function to calculate canvas bounds
 */
function calculateCanvasBounds(items) {
	return items.reduce(
		(bounds, [x, y, w, h]) => ({
			minX: Math.min(bounds.minX, x),
			minY: Math.min(bounds.minY, y),
			maxX: Math.max(bounds.maxX, x + w),
			maxY: Math.max(bounds.maxY, y + h),
		}),
		{ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
	);
}

/**
 * Creates a spatial grid for faster neighbor lookups
 */
function createSpatialGrid(items, spacing) {
	const grid = new Map();
	const cellSize = spacing * 4; // Cell size based on spacing

	items.forEach((item) => {
		const [x, y, w, h] = item;
		const cellX = Math.floor(x / cellSize);
		const cellY = Math.floor(y / cellSize);

		// Add item to relevant grid cells
		for (let i = cellX - 1; i <= cellX + Math.ceil(w / cellSize); i += 1) {
			for (let j = cellY - 1; j <= cellY + Math.ceil(h / cellSize); j += 1) {
				const key = `${i},${j}`;
				if (!grid.has(key)) grid.set(key, []);
				grid.get(key).push(item);
			}
		}
	});

	return grid;
}

/**
 * Get nearby items from the spatial grid
 */
function getNearbyItemsFromGrid(item, grid) {
	const [x, y, w, h] = item;
	const cellSize = 40; // Should match createSpatialGrid cellSize
	const cellX = Math.floor(x / cellSize);
	const cellY = Math.floor(y / cellSize);

	const nearbyItems = new Set();

	// Check surrounding cells
	for (let i = cellX - 1; i <= cellX + Math.ceil(w / cellSize); i += 1) {
		for (let j = cellY - 1; j <= cellY + Math.ceil(h / cellSize); j += 1) {
			const key = `${i},${j}`;
			const cellItems = grid.get(key) || [];
			cellItems.forEach((cellItem) => nearbyItems.add(cellItem));
		}
	}

	return Array.from(nearbyItems);
}

/**
 * Partial sort implementation (faster than full sort for large arrays)
 */
function partialSort(arr, k) {
	return arr.sort((a, b) => a.distance - b.distance).slice(0, k);
}

/**
 * Gets nearest items using distance-based sorting
 */
function getNearestItems(referencePoint, items, limit) {
	const itemsWithDistances = items.map((item) => ({
		item,
		distance: getDistance(getCenterPoint(item), referencePoint),
	}));

	return partialSort(itemsWithDistances, limit).map(({ item }) => item);
}

/**
 * Generate position candidates around an item
 */
function getPositionCandidates(item, [width, height], spacing) {
	const [ix, iy, iw, ih] = item;

	return [
		[ix, iy + ih + spacing], // Bottom
		[ix + iw + spacing, iy], // Right
		[ix, iy - height - spacing], // Top
		[ix - width - spacing, iy], // Left
	];
}

/**
 * Fast position validation using bounds set
 */
function isPositionValidFast(position, nearbyBounds, [width, height], spacing) {
	const [x, y] = position;
	const proposedBounds = [x, y, width, height];

	return !Array.from(nearbyBounds).some((itemBounds) => {
		const [ix, iy, iw, ih] = itemBounds.split(',').map(Number);
		return checkOverlap(proposedBounds, [ix, iy, iw, ih], spacing);
	});
}

/**
 * Find valid position around a specific item with optimized checking
 */
function findValidPositionAroundItemFast(item, nearbyItems, dimensions, spacing) {
	const positions = getPositionCandidates(item, dimensions, spacing);

	const nearbyBounds = new Set(nearbyItems.map((nearbyItem) => nearbyItem.join(',')));

	// eslint-disable-next-line no-restricted-syntax
	for (const pos of positions) {
		if (isPositionValidFast(pos, nearbyBounds, dimensions, spacing)) {
			return pos;
		}
	}

	return null;
}

/**
 * Find fallback position when no valid position is found
 */
function findFallbackPosition(existingItems, _, spacing) {
	const bounds = calculateCanvasBounds(existingItems);
	return [bounds.minX, bounds.maxY + spacing];
}

/**
 * Main optimized position finding function
 */
export function findOptimalPosition(
	existingItems,
	newItemDimensions,
	// eslint-disable-next-line default-param-last
	spacing = 10,
	viewportCenter,
	selectedItem = null,
) {
	const [width, height] = newItemDimensions;

	// Handle empty canvas case
	if (existingItems.length === 0) {
		const center = viewportCenter;
		return [center.x - width / 2, center.y - height / 2];
	}

	// Create spatial grid for optimization
	const grid = createSpatialGrid(existingItems, spacing);

	// Get reference point (selected item or viewport center)
	const referencePoint = selectedItem ? getCenterPoint(selectedItem) : viewportCenter;

	// Get nearest items to consider
	const searchItems = getNearestItems(referencePoint, existingItems, 20);

	// If selected item exists, try it first
	if (selectedItem) {
		const nearbyItems = getNearbyItemsFromGrid(selectedItem, grid);
		const position = findValidPositionAroundItemFast(
			selectedItem,
			nearbyItems,
			[width, height],
			spacing,
		);
		if (position) return position;
	}

	// Try each nearby item
	// eslint-disable-next-line no-restricted-syntax
	for (const item of searchItems) {
		const nearbyItems = getNearbyItemsFromGrid(item, grid);
		const position = findValidPositionAroundItemFast(item, nearbyItems, [width, height], spacing);
		if (position) return position;
	}

	// Fallback position
	return findFallbackPosition(existingItems, [width, height], spacing);
}

// // Test the implementation
// function runTest() {
// 	// Test viewport
// 	const viewport = {
// 		x: 0,
// 		y: 0,
// 		width: 800,
// 		height: 600,
// 	};
//
// 	// Generate test items (random positions)
// 	const testItems = Array.from({ length: 2000 }, () => {
// 		const x = Math.random() * viewport.width;
// 		const y = Math.random() * viewport.height;
// 		return [x, y, 50, 50]; // [x, y, width, height]
// 	});
//
// 	// New item dimensions
// 	const newItemDimensions = [50, 50];
//
// 	// Selected item (optional)
// 	const selectedItem = testItems[0];
//
// 	console.time('Position Finding');
// 	const position = findOptimalPosition(
// 		testItems,
// 		newItemDimensions,
// 		10, // spacing
// 		viewport,
// 		selectedItem,
// 	);
// 	console.timeEnd('Position Finding');
//
// 	console.log('Found position:', position);
//
// 	// Validate the position
// 	const [x, y] = position;
// 	const newItem = [x, y, ...newItemDimensions];
// 	const hasOverlap = testItems.some((item) => checkOverlap(newItem, item, 10));
//
// 	console.log('Position is valid (no overlaps):', !hasOverlap);
// }

// Run the test
// runTest();

/**
 * Finds a valid position around a specific item
 */
function findValidPositionAroundItem(item, allItems, [width, height], spacing) {
	const [ix, iy, iw, ih] = item;

	// Define positions to try (in priority order)
	const candidatePositions = [
		// Bottom
		{
			x: ix,
			y: iy + ih + spacing,
			alignment: 'vertical',
		},
		// Right
		{
			x: ix + iw + spacing,
			y: iy,
			alignment: 'horizontal',
		},
		// Top
		{
			x: ix,
			y: iy - height - spacing,
			alignment: 'vertical',
		},
		// Left
		{
			x: ix - width - spacing,
			y: iy,
			alignment: 'horizontal',
		},
	];

	// eslint-disable-next-line no-restricted-syntax
	for (const position of candidatePositions) {
		const proposedBounds = [position.x, position.y, width, height];

		// Check if position is valid (no overlaps)
		const isValid = !allItems.some((otherItem) => {
			const isSameItem = otherItem.join(',') === item.join(',');
			return !isSameItem && checkOverlap(proposedBounds, otherItem, spacing);
		});

		if (isValid) {
			return [position.x, position.y];
		}
	}

	return null;
}

/**
 * Finds optimal position for new items using nearest neighbor search
 * @param {Array<number[]>} existingItems - Array of [x,y,width,height] for existing items
 * @param {number[]} newItemDimensions - [width,height] of space needed
 * @param {number} spacing - Required spacing between items
 * @param {Object} viewportCenter - {x, y} of viewport
 * @param {number[]|null} selectedItem - Optional [x,y,width,height] of selected item
 * @returns {number[]} - [x,y] optimal position for new items
 */
export function findOptimalPositionAlt(
	existingItems,
	newItemDimensions,
	// eslint-disable-next-line default-param-last
	spacing = 10,
	viewportCenter,
	selectedItem = null,
) {
	const [requiredWidth, requiredHeight] = newItemDimensions;

	// If no existing items, center in viewport
	if (existingItems.length === 0) {
		return [viewportCenter.x - requiredWidth / 2, viewportCenter.y - requiredHeight / 2];
	}

	// Sort items by distance from reference point (selected item or viewport center)
	const referencePoint = selectedItem
		? { x: selectedItem[0] + selectedItem[2] / 2, y: selectedItem[1] + selectedItem[3] / 2 }
		: viewportCenter;

	const sortedItems = [...existingItems].sort((a, b) => {
		const distA = getDistance(getCenterPoint(a), referencePoint);
		const distB = getDistance(getCenterPoint(b), referencePoint);
		return distA - distB;
	});

	if (!selectedItem) {
		// If no selected item, try to find a position at the reference point
		const position = findValidPositionAroundItem(
			[referencePoint.x, referencePoint.y, 0, 0],
			sortedItems,
			[newItemDimensions[0], newItemDimensions[1]],
			spacing,
		);
		if (position) return position;
	}

	// Start with selected item if it exists
	if (selectedItem) {
		const position = findValidPositionAroundItem(
			selectedItem,
			sortedItems,
			[requiredWidth, requiredHeight],
			spacing,
		);
		if (position) return position;
	}
	const MAX_SEARCH_LENGTH = 20;
	let currentIndex = 0;
	// Try each item in order of proximity
	// eslint-disable-next-line no-restricted-syntax
	for (const item of sortedItems) {
		if (currentIndex > MAX_SEARCH_LENGTH) break;
		const position = findValidPositionAroundItem(
			item,
			sortedItems,
			[requiredWidth, requiredHeight],
			spacing,
		);
		if (position) return position;
		currentIndex += 1;
	}

	// Fallback: Return position at the bottom of all items
	const bounds = calculateCanvasBounds(existingItems);
	return [viewportCenter.x, bounds.maxY + spacing];
}
